/**
 * Pictures and drawn glyphs.
 *
 * ## The cover is the product
 *
 * `Cover` is a 3:4 frame for every product picture on every page, whatever the file behind it, so a
 * grid of covers is a grid and not a ragged edge — `object-fit: cover` crops, and a cover crop is
 * always safer than a letterbox. A product with no picture gets its INITIALS on the brand's surface,
 * never a stock photograph and never an empty frame (CHECKLIST §16b): a monogram reads as "no picture
 * yet", a placeholder photograph reads as a product that looks like something it is not.
 *
 * ## Glyphs are drawn, never typed
 *
 * `🛍` renders as a different picture on every OS, and `⌕` is missing from most faces a brand can
 * pick, so it shows as a tofu box in the corner of the one control on the page. Every icon anubis
 * needs is an inline SVG in `currentColor`.
 */
import type { ReactNode } from 'react';

import { cx, mod } from './cx';
import { Tooltip } from './tooltip';

/** The first letters of up to two words — the monogram a coverless product wears. */
export function initials(title: string | null | undefined): string {
  return (title ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

/**
 * A product picture in its 3:4 frame.
 *
 * `size` is the frame: `fill` takes its container's width (a card, a hero aside), the rest are fixed
 * thumbnails for rows — `md` a checkout line, `sm` a receipt, `xs` a docked bar. `frame` adds the
 * product-page treatment: a larger radius, the lifted shadow and a wash of the accent behind it.
 * `badge` is pinned to the corner — a discount — inside the frame on a card, outside on a hero.
 */
export function Cover({
  src,
  title,
  size = 'fill',
  frame,
  badge,
  lazy,
  monogram = true,
}: {
  src?: string | null | undefined;
  /** The product's name: the image's alt text, and the monogram when there is no image. */
  title?: string | null | undefined;
  size?: 'fill' | 'md' | 'sm' | 'xs' | undefined;
  frame?: 'card' | 'hero' | undefined;
  badge?: ReactNode | undefined;
  lazy?: boolean | undefined;
  /** Draw the initials when there is no image. Off for a decorative thumbnail. */
  monogram?: boolean | undefined;
}) {
  return (
    <div className={cx('a-cover-wrap', mod('a-cover-wrap', 'frame', frame))}>
      <div className={cx('a-cover', mod('a-cover', 'size', size), mod('a-cover', 'frame', frame))}>
        {src ? (
          <img
            className="a-cover__image"
            src={src}
            alt={title ?? ''}
            loading={lazy ? 'lazy' : undefined}
            decoding="async"
          />
        ) : monogram && size === 'fill' ? (
          <span className="a-cover__initials" aria-hidden="true">
            {initials(title)}
          </span>
        ) : null}
      </div>
      {badge ? <span className="a-cover-wrap__badge">{badge}</span> : null}
    </div>
  );
}

/**
 * A picture shown whole, never cropped — a free gift's book cover beside its promise. Full width up
 * to 200px on a phone (a 56px cover is a grey smudge there), a 96px thumbnail from 640px.
 */
/**
 * A product SHOWN (2026-10-16): the picture of it inside a tablet or a phone (`products`'
 * showcase), as the hero of a page — the free page's left column. Shown whole from 768px; on a
 * phone, where it stands above the form, it is cut to its top so the form is still in reach.
 */
export function Showcase({ src, alt = '' }: { src: string; alt?: string }) {
  return (
    <div className="a-showcase">
      <img className="a-showcase__image" src={src} alt={alt} decoding="async" />
    </div>
  );
}

export function Picture({ src, alt = '' }: { src: string; alt?: string }) {
  return <img className="a-picture" src={src} alt={alt} loading="lazy" decoding="async" />;
}

export type IconName =
  | 'bag'
  | 'search'
  | 'check'
  | 'chevron'
  | 'shield'
  | 'back'
  | 'store'
  | 'google'
  | 'linkedin'
  | 'facebook'
  | 'x'
  // The conversation set (2026-10-18, anubis-chat and anubis-media): what a post, a comment and a
  // picture viewer press.
  | 'heart'
  | 'heart-filled'
  | 'reply'
  | 'plus'
  | 'play'
  | 'close'
  | 'share'
  | 'download'
  | 'image'
  | 'send'
  | 'trash'
  | 'more'
  | 'next'
  | 'star'
  | 'star-filled'
  | 'people'
  | 'link'
  | 'edit'
  | 'pause'
  | 'sound'
  | 'mute'
  | 'expand'
  | 'collapse'
  // A question asked (2026-10-20, `HelpButton`): the way to `help`.
  | 'help'
  // A game at a glance (2026-10-20, `Marks` and `Picks`): how long, how heavy, for whom, and the
  // funnel that filters by them.
  | 'clock'
  | 'puzzle'
  | 'user'
  | 'child'
  // Who a game is for (2026-10-20): a grown-up with a child, and a group of grown-ups.
  | 'family'
  | 'group'
  | 'filter'
  // A notice that went wrong (2026-10-20, the isis toast's glyph).
  | 'warning';

const PATHS: Record<IconName, ReactNode> = {
  bag: <path d="M6 8h12l-1 11H7L6 8Zm3 0V6a3 3 0 0 1 6 0v2" />,
  search: <path d="M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm4.8 11.3L20 20" />,
  check: <path d="M5 13l4.5 4.5L19 7" />,
  chevron: <path d="M6 9l6 6 6-6" />,
  back: <path d="M15 6l-6 6 6 6" />,
  store: <path d="M4 9l1.5-4h13L20 9M4 9v10h16V9M4 9h16M9 19v-5h6v5" />,
  // The four sign-in networks (2026-10-05, `login`): filled marks in the current colour.
  google: (
    <path
      className="a-icon__fill"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09ZM12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23ZM5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84ZM12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
    />
  ),
  linkedin: (
    <path
      className="a-icon__fill"
      d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13Zm1.78 13.02H3.56V9h3.56v11.45ZM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z"
    />
  ),
  facebook: (
    <path
      className="a-icon__fill"
      d="M24 12.07C24 5.45 18.63.07 12 .07S0 5.45 0 12.07c0 5.99 4.39 10.95 10.13 11.85v-8.38H7.08v-3.47h3.05V9.43c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95h-1.51c-1.49 0-1.96.93-1.96 1.87v2.25h3.33l-.53 3.47h-2.8v8.38C19.61 23.03 24 18.06 24 12.07Z"
    />
  ),
  x: (
    <path
      className="a-icon__fill"
      d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.22-6.82-5.97 6.82H1.68l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23Zm-1.16 17.52h1.83L7.08 4.13H5.12l11.96 15.64Z"
    />
  ),
  heart: <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" />,
  'heart-filled': (
    <path
      className="a-icon__fill"
      d="M12 20.5s-7.5-4.6-7.5-10.5a4.3 4.3 0 0 1 7.5-2.9 4.3 4.3 0 0 1 7.5 2.9c0 5.9-7.5 10.5-7.5 10.5Z"
    />
  ),
  reply: <path d="M5 5h14v10H10l-4 4v-4H5V5Z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  play: (
    <path
      className="a-icon__fill"
      d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z"
    />
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  share: <path d="M12 4v11M8 8l4-4 4 4M6 12v7h12v-7" />,
  download: <path d="M12 4v11M8 11l4 4 4-4M5 20h14" />,
  image: <path d="M4 5h16v14H4V5Zm0 11 4.5-4.5 3.5 3.5 2.5-2.5L20 18M15.5 9.5h.01" />,
  send: <path d="M4 12 20 4l-5 16-3.5-6.5L4 12Zm7.5 1.5L20 4" />,
  trash: <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" />,
  more: <path d="M6 12h.01M12 12h.01M18 12h.01" />,
  next: <path d="M9 6l6 6-6 6" />,
  star: <path d="m12 4 2.4 5 5.4.6-4 3.7 1.1 5.3L12 16l-4.9 2.6 1.1-5.3-4-3.7 5.4-.6L12 4Z" />,
  'star-filled': (
    <path
      className="a-icon__fill"
      d="m12 3.5 2.6 5.3 5.8.7-4.3 4 1.2 5.7L12 16.4l-5.3 2.8 1.2-5.7-4.3-4 5.8-.7L12 3.5Z"
    />
  ),
  people: (
    <path d="M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5 8a5 5 0 0 1 10 0M16 11a2.5 2.5 0 1 0 0-5M17 14a4 4 0 0 1 3.5 5" />
  ),
  link: (
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  ),
  shield: (
    <path
      className="a-icon__fill"
      d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Zm-1.2 13.2-3.1-3.1 1.4-1.4 1.7 1.7 4.1-4.1 1.4 1.4-5.5 5.5Z"
    />
  ),
  edit: <path d="M4 20h4L18.5 9.5a2.12 2.12 0 0 0-3-3L5 17v3Zm9.5-11.5 3 3" />,
  pause: <path className="a-icon__fill" d="M7 5h3.5v14H7V5Zm6.5 0H17v14h-3.5V5Z" />,
  sound: (
    <path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4v-5Zm11.5-.5a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
  ),
  mute: <path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4v-5ZM16 10l4 4m0-4-4 4" />,
  expand: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  collapse: <path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" />,
  help: (
    <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm-2.5 6.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6M12 17h.01" />
  ),
  clock: <path d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM12 7.5V12l3 2" />,
  puzzle: <path d="M4 7h4.5a2.5 2.5 0 0 1 5 0H18v4.5a2.5 2.5 0 0 1 0 5V21H4V7Z" />,
  user: <path d="M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-7 9a7 7 0 0 1 14 0" />,
  child: <path d="M12 13.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM7.5 20a4.5 4.5 0 0 1 9 0" />,
  family: (
    <path d="M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-6 10a6 6 0 0 1 12 0M18.5 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM15 20a3.5 3.5 0 0 1 7 0" />
  ),
  group: (
    <path d="M5 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm-3.5 8a3.5 3.5 0 0 1 7 0M12 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm-3.5 8a3.5 3.5 0 0 1 7 0M19 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm-3.5 8a3.5 3.5 0 0 1 7 0" />
  ),
  filter: <path d="M4 5h16l-6 7.5V19l-4-2v-4.5L4 5Z" />,
  warning: <path d="M12 4 21 19.5H3L12 4Zm0 6v4.5M12 17h.01" />,
};

/** A drawn glyph in the current colour. Decorative — the control around it carries the name. */
export function Icon({
  name,
  size = 'md',
  tone = 'inherit',
}: {
  name: IconName;
  size?: 'sm' | 'md' | 'lg' | 'xl' | undefined;
  tone?: 'inherit' | 'accent' | 'accent-2' | 'muted' | 'positive' | undefined;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cx('a-icon', mod('a-icon', 'size', size), mod('a-tone', 'is', tone))}
    >
      {PATHS[name]}
    </svg>
  );
}

/** One fact in `Marks` (a `Mark` is the brand's logo). See there. */
export interface Fact {
  icon: IconName;
  /** Its value — the tooltip's large line, and what a screen reader hears: "About an hour", "4.5". */
  label: string;
  /** What the value is, written small ABOVE it in the tooltip: "Time", "Stars" (2026-10-21). */
  title?: string | undefined;
  /** Draw the glyph this many times — a scale of one to three (two clocks: about an hour). */
  repeat?: number | undefined;
  /** A number after the glyph: a score, how many hearts. */
  count?: ReactNode | undefined;
}

/**
 * A quiet row of small glyphs that tells a few facts at a glance, with no words (2026-10-20, Wall:
 * *"icons instead of text"*): two clocks, three puzzle pieces, a star and its score, a heart and its
 * count. Every glyph in the accent — one colour, never a traffic light (owner, 2026-10-20). Each
 * fact's `label` is read out, and shown in a `Tooltip` on hover, on focus and at a press — a phone
 * learns what a glyph means by pressing it (owner, 2026-10-21). Nothing to show draws nothing.
 */
export function Marks({ items, label }: { items: readonly Fact[]; label: string }) {
  if (items.length === 0) return null;
  return (
    <ul className="a-marks" aria-label={label}>
      {items.map((item) => (
        <li key={`${item.icon}-${item.label}`} className="a-marks__item">
          <Tooltip
            text={
              item.title ? (
                <>
                  <span className="a-tooltip__title">{item.title}</span>
                  <span className="a-tooltip__value">{item.label}</span>
                </>
              ) : (
                item.label
              )
            }
          >
            <span className="a-marks__glyphs" aria-hidden="true">
              {Array.from({ length: Math.max(1, Math.min(5, item.repeat ?? 1)) }, (_, index) => (
                <Icon key={index} name={item.icon} size="sm" tone="accent" />
              ))}
            </span>
            {item.count !== undefined && item.count !== null ? (
              <span className="a-marks__count" aria-hidden="true">
                {item.count}
              </span>
            ) : null}
            <span className="a-visually-hidden">
              {item.title ? `${item.title}: ${item.label}` : item.label}
            </span>
          </Tooltip>
        </li>
      ))}
    </ul>
  );
}

/**
 * The success mark: a tick in a circle, in the fixed positive green (see `tokens.css`), centred above
 * a confirmation's title. A paragraph saying "check your inbox" is read; a tick is SEEN, and it is
 * what tells somebody on a phone that the press worked before they read a word.
 */
export function Tick() {
  return (
    <span className="a-tick" aria-hidden="true">
      <svg viewBox="0 0 72 72" className="a-tick__svg">
        <circle cx="36" cy="36" r="34" className="a-tick__halo" />
        <circle cx="36" cy="36" r="26" className="a-tick__disc" />
        <path d="M24 37.5 L32.5 46 L48.5 29" className="a-tick__mark" />
      </svg>
    </span>
  );
}
